import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Building2, CheckCircle2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import SigningMethodPanel from './SigningMethodPanel';

const emptyForm = {
  label: '',
  company_name: '',
  org_number: '',
  vat_number: '',
  f_skatt: true,
  address: '',
  postal_code: '',
  city: '',
  country: 'Sverige',
  email: '',
  phone: '',
  website: '',
  logo_url: '',
  bankgiro: '',
  plusgiro: '',
  iban: '',
  bic: '',
  swish: '',
  show_bankgiro: true,
  show_plusgiro: true,
  show_iban: true,
  show_swish: false,
  invoice_prefix: 'F',
  next_invoice_number: 1,
  default_payment_terms_days: 30,
  default_vat_rate: 25.0,
  default_currency: 'SEK',
  invoice_footer: '',
  is_default: false,
  // Autopåminnelser
  auto_reminders_enabled: false,  // Default AV
  reminder_days_after_due: [3, 10, 20],
};

const BillingProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchProfiles(); }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await api.listBillingProfiles();
      setProfiles(res.data || []);
    } catch (err) {
      toast.error('Kunde inte hämta fakturabolag');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyForm, ...p });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.label) { toast.error('Internt namn krävs'); return; }
    if (!form.company_name) { toast.error('Företagsnamn krävs'); return; }
    if (!form.org_number) { toast.error('Organisationsnummer krävs'); return; }
    // Minst ett betalsätt måste finnas
    const hasPayment = [form.bankgiro, form.plusgiro, form.iban, form.swish].some(v => (v || '').trim());
    if (!hasPayment) {
      toast.error('Ange minst ett betalningssätt (IBAN, Bankgiro, Plusgiro eller Swish)');
      return;
    }
    setSaving(true);
    try {
      // Rensa payloaden — konvertera tomma strängar till null ENDAST för fält som
      // backend har som Optional. Fält som är `str = ""` måste förbli tomma strängar
      // annars svarar backend med 422 (Pydantic) och sonner kraschar på error-arrayen.
      const NULLABLE_FIELDS = new Set([
        'email', 'phone', 'website', 'logo_url', 'vat_number',
        'bankgiro', 'plusgiro', 'iban', 'bic', 'swish',
        'invoice_footer',
      ]);
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' && NULLABLE_FIELDS.has(k)) payload[k] = null;
      });
      if (editing) {
        await api.updateBillingProfile(editing.id, payload);
        toast.success(`${form.label} uppdaterat`);
      } else {
        await api.createBillingProfile(payload);
        toast.success(`${form.label} skapat`);
      }
      setShowForm(false);
      fetchProfiles();
    } catch (err) {
      toast.error(formatApiError(err, 'Kunde inte spara'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Radera fakturabolag "${p.label}"?`)) return;
    try {
      await api.deleteBillingProfile(p.id);
      toast.success('Raderat');
      fetchProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte radera');
    }
  };

  const handleSetDefault = async (p) => {
    try {
      await api.setDefaultBillingProfile(p.id);
      toast.success(`${p.label} är nu standard`);
      fetchProfiles();
    } catch (err) {
      toast.error('Kunde inte sätta som standard');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl" data-testid="billing-profiles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 size={22} className="text-[#E8613A]" /> Fakturabolag
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lägg upp alla bolag du fakturerar från. Vid fakturaskapande kan du välja vilket bolag som ska stå som avsändare.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="bp-new-btn">
          <Plus size={14} className="mr-1.5" /> Nytt fakturabolag
        </Button>
      </div>

      <SigningMethodPanel />

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Laddar…</div>
      ) : profiles.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-10 text-center text-muted-foreground text-sm">
          Inga fakturabolag ännu. Klicka på &quot;Nytt fakturabolag&quot; för att börja.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <div
              key={p.id}
              className={`bg-card border-2 rounded-xl p-5 transition-all ${
                p.is_default ? 'border-[#E8613A] shadow-sm' : 'border-border'
              }`}
              data-testid={`bp-row-${p.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-foreground truncate">{p.label}</h3>
                    {p.is_default && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-[#E8613A]/10 text-[#E8613A] rounded">
                        <Star size={10} /> Standard
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{p.company_name}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground" data-testid={`bp-edit-${p.id}`}>
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => handleDelete(p)} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 dark:hover:bg-red-950" data-testid={`bp-delete-${p.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Org.nr</span><span className="font-medium">{p.org_number}</span></div>
                {p.bankgiro && <div className="flex justify-between"><span className="text-muted-foreground">Bankgiro</span><span className="font-medium">{p.bankgiro}</span></div>}
                {p.iban && <div className="flex justify-between"><span className="text-muted-foreground">IBAN</span><span className="font-medium truncate ml-2">{p.iban}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Fakturaprefix</span><span className="font-medium">{p.invoice_prefix}-{p.next_invoice_number}</span></div>
              </div>
              {!p.is_default && (
                <button
                  onClick={() => handleSetDefault(p)}
                  className="w-full mt-4 text-xs font-semibold text-[#E8613A] hover:bg-[#E8613A]/5 rounded-md py-2 transition-colors flex items-center justify-center gap-1.5"
                  data-testid={`bp-set-default-${p.id}`}
                >
                  <Star size={12} /> Sätt som standard
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Redigera fakturabolag' : 'Nytt fakturabolag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Internt namn (visas i fakturaval) *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Markbygg Norr" className="mt-1" data-testid="bp-label" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Företagsnamn (på fakturan) *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" data-testid="bp-company-name" />
              </div>
              <div>
                <Label className="text-xs">Organisationsnummer *</Label>
                <Input value={form.org_number} onChange={(e) => setForm({ ...form, org_number: e.target.value })} placeholder="556789-1234" className="mt-1" data-testid="bp-org-number" />
              </div>
              <div>
                <Label className="text-xs">Momsnummer</Label>
                <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="SE556789123401" className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={form.f_skatt} onCheckedChange={(v) => setForm({ ...form, f_skatt: v })} />
                <Label className="text-sm">Innehar F-skatt</Label>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">Adress</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Postnummer</Label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Stad</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">E-post</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
              </div>
            </div>

            {/* BETALSÄTT — minst ett krävs, kunden väljer vilka som visas på fakturan */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Betalningssätt (minst ett) *</Label>
                <p className="text-[10px] text-muted-foreground">Bocka i &quot;Visa&quot; för de sätt som ska stå på fakturan</p>
              </div>
              <div className="space-y-2">
                {/* IBAN */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">IBAN</Label>
                      <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="SE45 5000 0000 0583 9825 7466" className="mt-1" data-testid="bp-iban" />
                    </div>
                    <div><Label className="text-xs">BIC/SWIFT</Label>
                      <Input value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 pt-6 text-xs whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={form.show_iban} onChange={(e) => setForm({ ...form, show_iban: e.target.checked })}
                      className="w-4 h-4 accent-[#E8613A]" data-testid="bp-show-iban" />
                    Visa
                  </label>
                </div>
                {/* Bankgiro */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Bankgiro</Label>
                    <Input value={form.bankgiro} onChange={(e) => setForm({ ...form, bankgiro: e.target.value })} placeholder="5050-1234" className="mt-1" data-testid="bp-bankgiro" />
                  </div>
                  <label className="flex items-center gap-1.5 pt-6 text-xs whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={form.show_bankgiro} onChange={(e) => setForm({ ...form, show_bankgiro: e.target.checked })}
                      className="w-4 h-4 accent-[#E8613A]" data-testid="bp-show-bankgiro" />
                    Visa
                  </label>
                </div>
                {/* Plusgiro */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Plusgiro</Label>
                    <Input value={form.plusgiro} onChange={(e) => setForm({ ...form, plusgiro: e.target.value })} className="mt-1" />
                  </div>
                  <label className="flex items-center gap-1.5 pt-6 text-xs whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={form.show_plusgiro} onChange={(e) => setForm({ ...form, show_plusgiro: e.target.checked })}
                      className="w-4 h-4 accent-[#E8613A]" />
                    Visa
                  </label>
                </div>
                {/* Swish */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Swish (företagsnummer)</Label>
                    <Input value={form.swish} onChange={(e) => setForm({ ...form, swish: e.target.value })} placeholder="1231234567" className="mt-1" data-testid="bp-swish" />
                  </div>
                  <label className="flex items-center gap-1.5 pt-6 text-xs whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={form.show_swish} onChange={(e) => setForm({ ...form, show_swish: e.target.checked })}
                      className="w-4 h-4 accent-[#E8613A]" data-testid="bp-show-swish" />
                    Visa
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border pt-4">
              <div>
                <Label className="text-xs">Faktura-prefix</Label>
                <Input value={form.invoice_prefix} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} placeholder="F" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Nästa fakturanummer</Label>
                <Input type="number" value={form.next_invoice_number} onChange={(e) => setForm({ ...form, next_invoice_number: parseInt(e.target.value) || 1 })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Standard betalningstid (dagar)</Label>
                <Input type="number" value={form.default_payment_terms_days} onChange={(e) => setForm({ ...form, default_payment_terms_days: parseInt(e.target.value) || 30 })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Standard momssats (%)</Label>
                <Input type="number" step="0.1" value={form.default_vat_rate} onChange={(e) => setForm({ ...form, default_vat_rate: parseFloat(e.target.value) || 25 })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Sidfot på faktura (valfritt)</Label>
              <textarea value={form.invoice_footer} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })}
                rows={2}
                className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2" />
            </div>
            <div className="flex items-center gap-2 pt-1 border-t border-border pt-3">
              <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="bp-is-default" />
              <Label className="text-sm">Sätt som standard-bolag (förvalt vid fakturaskapande)</Label>
            </div>

            {/* Autopåminnelser */}
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!form.auto_reminders_enabled}
                  onCheckedChange={(v) => setForm({ ...form, auto_reminders_enabled: v })}
                  data-testid="bp-auto-reminders"
                />
                <Label className="text-sm">Skicka automatiska påminnelser till kunder</Label>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Default AV — sluta stressa kunderna med för många mejl. När du aktiverar kan du välja hur många dagar EFTER förfallodatum påminnelser skickas.
              </p>
              {form.auto_reminders_enabled && (
                <div>
                  <Label className="text-xs">Dagar efter förfallodag (upp till 3 stycken, komma-separerat)</Label>
                  <Input
                    value={(form.reminder_days_after_due || []).join(', ')}
                    onChange={(e) => {
                      const days = e.target.value
                        .split(',')
                        .map(s => parseInt(s.trim(), 10))
                        .filter(n => Number.isFinite(n) && n >= 0 && n <= 365)
                        .slice(0, 3);
                      setForm({ ...form, reminder_days_after_due: days });
                    }}
                    placeholder="T.ex. 3, 10, 20"
                    className="mt-1"
                    data-testid="bp-reminder-days"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Rekommenderat: <strong>3, 10, 20</strong> — snäll ton först, sedan bestämd, sedan sista påminnelse.
                    Inkasso-steget skickas ALDRIG automatiskt (kräver alltid manuellt val).
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Avbryt</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="bp-save-btn">
              {saving ? 'Sparar…' : editing ? 'Spara ändringar' : 'Skapa fakturabolag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingProfiles;
